import type { DeploymentDatabaseChanges, DetectedOperation, MagentoDBStatus, MagentoPatch } from '../api';
import { plural } from '../labels';
import { DatabaseSchemaChanges, schemaUnknowns } from './DatabaseSchemaChanges';
import { DatabaseCodeView } from './DatabaseCodeChanges';

/**
 * DatabaseChangesPanel (observation 1): what a Magento release changes in
 * the database, in Magento's own words. Each of setup:db:status's checks
 * is one row, the patches it names are listed with their module, and the
 * panel says whether setup:upgrade applied them. It names what cannot be
 * known (individual table changes, what a data patch writes) instead of
 * implying it.
 */

const validatorRows: { kind: string; title: string; what: string }[] = [
  { kind: 'declarative_schema', title: 'Declarative schema', what: 'Tables, columns, indexes and keys defined in the modules’ db_schema.xml' },
  { kind: 'schema_patches', title: 'Schema patches', what: 'One-off code that changes table structure' },
  { kind: 'data_patches', title: 'Data patches', what: 'One-off code that writes configuration or records; no table structure' },
  { kind: 'module_versions', title: 'Module versions', what: 'Older modules that upgrade by setup_version in module.xml' },
];

/** What each kind the release comparison reports is called on the page. */
const detectedKinds: Record<string, string> = {
  schema: 'Schema',
  schema_patch: 'Schema patch',
  data_patch: 'Data patch',
  schema_file: 'Schema declaration',
  setup_script: 'Setup script',
  module_version: 'Module version',
  module_declaration: 'Module declaration',
  module_enable: 'Module enabled',
  module_disable: 'Module disabled',
  config_import: 'Configuration import',
  config_php: 'Module list and configuration',
  env_php: 'Environment setting',
  platform_version: 'Magento version',
};

function detectedKind(kind: string): string {
  return detectedKinds[kind] ?? kind.replace(/_/g, ' ');
}

function shortPatchName(patch: string): string {
  const parts = patch.split('\\');
  return parts[parts.length - 1] || patch;
}

function pendingCount(status: MagentoDBStatus, kind: string): number {
  switch (kind) {
    case 'schema_patches':
      return status.unapplied_schema_patches.length;
    case 'data_patches':
      return status.unapplied_data_patches.length;
    case 'module_versions':
      return status.module_version_changes.length;
    default:
      return 0;
  }
}

/** True when anything structural (not only data) is pending. */
export function changesStructure(status: MagentoDBStatus | undefined): boolean {
  if (!status) return false;
  const outOf = (kind: string) => status.validators.some((v) => v.kind === kind && !v.up_to_date);
  return (
    outOf('declarative_schema') ||
    outOf('schema_patches') ||
    status.unapplied_schema_patches.length > 0 ||
    status.module_version_changes.some((change) => change.type === 'schema')
  );
}

function appliedSentence(changes: DeploymentDatabaseChanges): string | null {
  const when = changes.applied_at ? ` · ${new Date(changes.applied_at).toLocaleTimeString()}` : '';
  switch (changes.applied) {
    case 'applied':
      return changes.applied_action === 'install' ? `setup:install created the schema${when}` : `setup:upgrade applied${when}`;
    case 'not_needed':
      return 'setup:upgrade not run · nothing to apply';
    case 'running':
      return 'setup:upgrade running';
    case 'pending':
      return 'Not applied yet · setup:upgrade runs later in this deployment';
    case 'failed':
      return 'setup:upgrade failed, so changes may be partly applied. The step’s output says how far it got.';
    default:
      return 'Not applied · the deployment ended before setup:upgrade';
  }
}

function PatchList({ patches }: { patches: MagentoPatch[] }) {
  return (
    <ul className="db-changes__patches">
      {patches.map((patch) => (
        <li key={patch.patch}>
          <span className="db-changes__patch-name" translate="no">{shortPatchName(patch.patch)}</span>
          {patch.module ? <span className="db-changes__patch-module">{patch.module}</span> : null}
          <code className="db-changes__patch-class" translate="no">{patch.patch}</code>
        </li>
      ))}
    </ul>
  );
}

function DetectedList({ operations }: { operations: DetectedOperation[] }) {
  return (
    <ul className="db-changes__patches">
      {operations.map((operation) => (
        <li key={`${operation.kind}:${operation.subject}`}>
          <span className="db-changes__patch-name">{detectedKind(operation.kind)}</span>
          <span className="db-changes__patch-module">
            {operation.detail}
            {operation.known ? '' : ' (could not be determined)'}
          </span>
          <code className="db-changes__patch-class" translate="no">{operation.subject}</code>
        </li>
      ))}
    </ul>
  );
}

export function DatabaseChangesPanel({ changes }: { changes: DeploymentDatabaseChanges }) {
  const status = changes.status;
  const detected = changes.detected?.operations ?? [];
  const flagged = changes.overall === 'flagged';
  const structural = changesStructure(status);
  const hasPending =
    status !== undefined &&
    (status.validators.some((v) => !v.up_to_date) || status.unapplied_data_patches.length + status.unapplied_schema_patches.length + status.module_version_changes.length > 0);

  // The server's own list, plus what the schema comparison could not read.
  // Deployments before the comparison keep the server's table-level line.
  const unknowns = changes.schema
    ? [...changes.not_determinable.filter((line) => !line.startsWith('Which tables')), ...schemaUnknowns(changes.schema)]
    : changes.not_determinable;

  return (
    <div className="db-changes" data-testid="database-changes">
      {changes.state === 'pending' ? (
        <p className="panel__note" data-testid="database-changes-summary">
          Not analysed yet · Magento’s status check runs once the release is built
        </p>
      ) : changes.state === 'not_analysed' ? (
        <p className="panel__note" data-testid="database-changes-summary">
          Not analysed · the deployment ended before Magento’s status check ran
        </p>
      ) : changes.state === 'not_installed' ? (
        <p className="panel__note" data-testid="database-changes-summary">
          Empty database · installs the whole schema. {appliedSentence(changes)}
        </p>
      ) : changes.state === 'unrecognised' || !status ? (
        <p className="panel__note" data-testid="database-changes-summary">
          Status check output could not be read into details · see Magento’s output below
        </p>
      ) : (
        <>
          {hasPending || changes.applied === 'not_needed' ? (
            <p className="panel__note" data-testid="database-changes-summary">
              {appliedSentence(changes)}
            </p>
          ) : null}
          {/* Four facts side by side (CO-150): what each of Magento's checks
              found, and whether it is done, pending or not known. A change
              is an impact, not a problem: pending is amber only while the
              upgrade has not run. */}
          <ul className="db-metrics" aria-label="Magento’s database checks">
            {validatorRows.map((row) => {
              const verdict = status.validators.find((v) => v.kind === row.kind);
              const count = pendingCount(status, row.kind);
              // The checks ran before the upgrade; once setup:upgrade
              // succeeded, what was pending is applied.
              const applied = changes.applied === 'applied';
              const state = !verdict ? 'unknown' : verdict.up_to_date ? 'ok' : applied ? 'applied' : changes.applied === 'failed' ? 'failed' : 'pending';
              const word = !verdict
                ? 'Not reported'
                : verdict.up_to_date
                  ? 'Up to date'
                  : applied
                    ? count > 0 ? `${count} applied` : 'Applied'
                    : count > 0 ? `${count} to apply` : 'Changes to apply';
              const tone = { ok: 'neutral', applied: 'success', pending: 'info', failed: 'warning', unknown: 'neutral' }[state];
              return (
                <li key={row.kind} className={`db-metric db-metric--${state}`} data-testid={`db-check-${row.kind}`}>
                  <span className="db-metric__title">{row.title}</span>
                  <span className={`badge badge--${tone} db-metric__state`}>{word}</span>
                  <span className="db-metric__what">{row.what}</span>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {/* The lists call this a database change because of what Commerce Ops'
          own comparison of the two releases found, which Magento's check
          does not report (CO-173): say so, and list it. */}
      {flagged ? (
        <p className="panel__note" data-testid="flagged-summary">
          <strong>Flagged as a database change.</strong> Magento’s checks: nothing to apply · Release comparison found{' '}
          {plural(detected.length, 'change')}, listed below · Code-only rollback is not offered, because the previous release is not
          assumed to run against the database as it is now.
        </p>
      ) : null}
      {detected.length > 0 ? (
        <details className="disclosure db-changes__section" open={flagged} data-testid="detected-changes">
          <summary>Found by comparing the two releases ({detected.length})</summary>
          <DetectedList operations={detected} />
        </details>
      ) : null}

      {changes.schema ? <DatabaseSchemaChanges schema={changes.schema} /> : null}
      {changes.code ? <DatabaseCodeView code={changes.code} /> : null}

      {status && status.unapplied_data_patches.length > 0 ? (
        <details className="disclosure db-changes__section" data-testid="data-changes">
          <summary>Data changes: {plural(status.unapplied_data_patches.length, 'data patch', 'data patches')}</summary>
          <PatchList patches={status.unapplied_data_patches} />
        </details>
      ) : null}
      {status && status.unapplied_schema_patches.length > 0 ? (
        <details className="disclosure db-changes__section">
          <summary>Schema patches ({status.unapplied_schema_patches.length})</summary>
          <PatchList patches={status.unapplied_schema_patches} />
        </details>
      ) : null}
      {status && status.module_version_changes.length > 0 ? (
        <details className="disclosure db-changes__section">
          <summary>Module versions ({status.module_version_changes.length})</summary>
          <ul className="db-changes__patches">
            {status.module_version_changes.map((change) => (
              <li key={`${change.module}:${change.type}`}>
                <span className="db-changes__patch-name">{change.module}</span>
                <span className="db-changes__patch-module">
                  {change.type} {change.current || 'none'} → {change.required}
                </span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {flagged ? null : changes.rollback_impact ? (
        <RollbackGuidance impact={changes.rollback_impact} />
      ) : changes.state !== 'pending' && changes.state !== 'not_analysed' ? (
        hasPending || changes.state === 'not_installed' ? (
          structural || changes.state === 'not_installed' ? (
            <p className="panel__note">
              <strong>Rolling back code does not roll back the database.</strong> Switching to the previous release does not undo
              the schema changes above. Commerce Ops takes a database checkpoint before the upgrade runs, so a restore is possible.
            </p>
          ) : (
            <p className="panel__note">
              <strong>No table structure changes.</strong> Data patches write records once; switching to the previous release does
              not undo what they wrote. The database checkpoint taken before the upgrade can restore it.
            </p>
          )
        ) : null
      ) : null}

      {unknowns.length > 0 && changes.state !== 'pending' ? (
        <details className="disclosure db-changes__section" open={changes.overall === 'undetermined'}>
          <summary>What this cannot tell</summary>
          <ul className="db-changes__unknowns">
            {unknowns.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </details>
      ) : null}
      {changes.raw ? (
        <details className="disclosure db-changes__section">
          <summary>Magento’s output</summary>
          <pre className="step-list__output">{changes.raw}</pre>
        </details>
      ) : null}
    </div>
  );
}

/**
 * RollbackGuidance: what a code rollback leaves behind, from the server's
 * rollback impact. Never says a rollback is safe; it says what remains.
 */
function RollbackGuidance({ impact }: { impact: NonNullable<DeploymentDatabaseChanges['rollback_impact']> }) {
  switch (impact) {
    case 'none':
      return null;
    case 'data':
      return (
        <p className="panel__note" data-testid="rollback-guidance">
          <strong>No table structure changes.</strong> Data patches write records once; switching to the previous release does not undo
          what they wrote. The database checkpoint taken before the upgrade can restore it.
        </p>
      );
    case 'additive':
      return (
        <p className="panel__note" data-testid="rollback-guidance">
          <strong>Additive schema change.</strong> New tables, columns or indexes stay when the code is rolled back; the previous release
          usually ignores them, but that is not guaranteed. The checkpoint taken before the upgrade restores the database as it was.
        </p>
      );
    case 'modifying':
      return (
        <p className="panel__note" data-testid="rollback-guidance">
          <strong>Existing structure changes.</strong> Columns or keys the previous release uses change shape, so rolling back the code alone
          may not work against the upgraded database. Restore the checkpoint taken before the upgrade together with the code.
        </p>
      );
    case 'destructive':
      return (
        <p className="panel__note" data-testid="rollback-guidance">
          <strong>Data is dropped.</strong> This release removes or narrows tables or columns; their data is gone once the upgrade runs and no
          code rollback brings it back. Only the checkpoint taken before the upgrade does.
        </p>
      );
    default:
      return (
        <p className="panel__note" data-testid="rollback-guidance">
          <strong>Rolling back code does not roll back the database.</strong> What this release changes is not fully known, so plan on the
          checkpoint taken before the upgrade rather than a code-only rollback.
        </p>
      );
  }
}
